import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
    useCurrentRapidData,
    useInitializeModelFromRapidData,
    useClearModelState,
    useAnnotationsForFileAndFrameId
} from "@rapid/store";
import { useCurrentWorkflow } from "@rapid/hooks/useCurrentWorkflow";
import { useCurrentWorkspace } from "@rapid/hooks/useCurrentWorkspace";
import { useUser } from "@rapid/contexts/UserContext";
import { ModelStatus } from "@rapid/components/flow/shared/ModelStatus/cms";
import { DataMode } from "@rapid/components/flow/shared/Deploy/documentation/types";
import { projectSelector } from "@rflocal/shared";
import {
    AvailableRapidModelArchitectures,
    RapidTrainStatus
} from "../components/flow/shared/Deploy/documentationCms";
import { useAnalytics } from "@rapid/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@rapid/analytics/types";
import { DeployContext, DeployContextType } from "./DeployContext";
import {
    type CreateRapidWorkflowProps,
    type BuildMetadata,
    BuildInferenceType
} from "@rapid/types/workflow";
import { useMedia } from "./MediaContext";
import { useVisibilityThroughWorkspace } from "@rapid/hooks/useVisibilityThroughWorkspace";
import { usePromptLabels } from "@rapid/store";
import { MediaItemStatus } from "@rapid/types/media";
import { useCreateWorkflow } from "@rapid/queries/workflowQueries";
import { uploadRapidSetupPayload } from "@rapid/utils/rapidSetupPayload";

export const DeployContextProvider = ({ children }: { children: React.ReactNode }) => {
    const { currentWorkspace, apiKey: workspaceApiKey } = useCurrentWorkspace();
    const apiKey = workspaceApiKey || "";
    const promptLabels = usePromptLabels();
    const annotationsForFileAndFrameId = useAnnotationsForFileAndFrameId();

    const { currentWorkflow: workflow, setCurrentWorkflow } = useCurrentWorkflow();
    const createWorkflowMutation = useCreateWorkflow();
    const creatingRapidWorkflow = createWorkflowMutation.isPending;
    const { track } = useAnalytics();
    const visibility = useVisibilityThroughWorkspace();
    const { mediaItems, storageUploadDir } = useMedia();

    // Keep only UI-specific state in context
    const [dataMode, setDataMode] = useState<DataMode>(DataMode.IMAGE);
    const [loading, setLoading] = useState<boolean>(false);

    const currentRapidData = useCurrentRapidData();
    const initializeModel = useInitializeModelFromRapidData();
    const clearModelState = useClearModelState();
    const user = useUser();

    // Track previous rapidData state to detect meaningful changes
    const prevRapidDataRef = useRef<{ id: string | null; modelChangesLength: number }>({
        id: null,
        modelChangesLength: 0
    });

    // Initialize model state from rapid data for metrics entry point
    // Only re-initialize when rapidData ID or modelChanges actually change
    useEffect(() => {
        if (!currentRapidData) {
            if (!workflow?.rapidDataId) {
                clearModelState();
                prevRapidDataRef.current = { id: null, modelChangesLength: 0 };
            }
            return;
        }

        if (!user?.uid) {
            return;
        }

        const currentId = currentRapidData.id;
        const currentModelChangesLength = currentRapidData.modelChanges?.length ?? 0;
        const prev = prevRapidDataRef.current;

        // Only initialize if ID changed or modelChanges array changed
        const hasIdChanged = currentId !== prev.id;
        const hasModelChangesChanged = currentModelChangesLength !== prev.modelChangesLength;

        if (hasIdChanged || hasModelChangesChanged) {
            initializeModel(currentRapidData, user.uid);
            prevRapidDataRef.current = {
                id: currentId,
                modelChangesLength: currentModelChangesLength
            };
        }
    }, [currentRapidData, user?.uid, workflow?.rapidDataId, initializeModel, clearModelState]);

    const modelStatus = useMemo(() => {
        if (!workflow || !currentRapidData) return ModelStatus.INITIALIZING;

        const isDeployed = !!currentRapidData?.lastExecutedAt;

        if (isDeployed) {
            return ModelStatus.DEPLOYED;
        }

        const allRfInstantModels =
            currentRapidData.models[AvailableRapidModelArchitectures.RF_INSTANT];
        const allRfDetrModels = currentRapidData.models[AvailableRapidModelArchitectures.RF_DETR];

        const latestRfInstantModel = allRfInstantModels[allRfInstantModels.length - 1];
        const latestRfDetrModel = allRfDetrModels[allRfDetrModels.length - 1];

        const latestRfInstantModelTrainStatus = latestRfInstantModel?.train?.status;
        const latestRfDetrModelTrainStatus = latestRfDetrModel?.train?.status;

        if (
            latestRfInstantModelTrainStatus === RapidTrainStatus.TRAINING ||
            latestRfDetrModelTrainStatus === RapidTrainStatus.TRAINING
        ) {
            return ModelStatus.TRAINING;
        }

        if (
            allRfInstantModels.length === 1 &&
            allRfDetrModels.length === 1 &&
            latestRfInstantModelTrainStatus === RapidTrainStatus.PENDING &&
            latestRfDetrModelTrainStatus === RapidTrainStatus.PENDING
        ) {
            return ModelStatus.INITIALIZING;
        }

        return ModelStatus.NOT_DEPLOYED;
    }, [workflow, currentRapidData]);

    const createRapidWorkflowDeploy = useCallback(
        async (props: Omit<CreateRapidWorkflowProps, "buildMetadata" | "exampleData">) => {
            try {
                track({
                    event: AnalyticsFlowEvent.CREATE_WORKFLOW_STARTED,
                    data: { task: props.task }
                });

                const exampleData = mediaItems
                    .filter(
                        (mediaItem) =>
                            mediaItem.status === MediaItemStatus.COMPLETED &&
                            mediaItem.uploadResult?.filename
                    )
                    .map((mediaItem) => ({
                        isVideo: mediaItem.isVideo,
                        filePath: mediaItem.uploadResult?.filename || mediaItem.id,
                        resizedFilePath: mediaItem.uploadResizedResult?.filename || null,
                        isSharable: visibility === "public"
                    }));
                if (exampleData.length === 0) {
                    throw new Error("Cannot create workflow without uploaded media items");
                }

                const hasBoxPrompts = Object.values(annotationsForFileAndFrameId).some((frames) =>
                    Object.values(frames).some((annotations) =>
                        annotations.some(
                            (annotation) =>
                                !annotation.draft &&
                                annotation.confidence == null &&
                                promptLabels.has(annotation.label)
                        )
                    )
                );
                const buildMetadata: BuildMetadata = { inferenceTypes: {}, hasBoxPrompts };
                const promptNamesMap: Record<string, string> = {};
                promptLabels.forEach((label) => {
                    buildMetadata.inferenceTypes[label.name] = BuildInferenceType.RAPID;
                    if (label.promptName) {
                        promptNamesMap[label.name] = label.promptName;
                    }
                });
                if (Object.keys(promptNamesMap).length > 0) {
                    buildMetadata.promptNames = promptNamesMap;
                }

                const {
                    instantPromptData,
                    groundTruthData,
                    predictionDatasetData,
                    predictionReviewData,
                    ...workflowProps
                } = props;
                const setupPayloadRef = await uploadRapidSetupPayload({
                    storageUploadDir,
                    instantPromptData,
                    groundTruthData,
                    predictionDatasetData,
                    predictionReviewData
                });

                const createdWorkflow = await createWorkflowMutation.mutateAsync({
                    ...workflowProps,
                    setupPayloadRef,
                    exampleData,
                    buildMetadata
                });

                setCurrentWorkflow(createdWorkflow);

                track({
                    event: AnalyticsFlowEvent.WORKFLOW_CREATED,
                    data: { task: props.task, workflow: createdWorkflow }
                });

                return createdWorkflow;
            } catch (error) {
                console.error("DeployProvider createRapidWorkflow error", error);
                throw error;
            }
        },
        [
            createWorkflowMutation,
            track,
            mediaItems,
            storageUploadDir,
            visibility,
            promptLabels,
            annotationsForFileAndFrameId,
            setCurrentWorkflow
        ]
    );

    const getWorkflowEndpoint = useCallback((workspaceUrl: string, workflowUrl: string) => {
        return projectSelector.workflowUrl(workspaceUrl, workflowUrl);
    }, []);

    const hasValidWorkflowCredentials = !!(workflow?.url && currentWorkspace?.url);

    const workflowCredentials = useMemo(() => {
        const workspaceUrl = currentWorkspace?.url || "your-workspace-url";
        const workflowUrl = workflow?.url || "your-workflow-url";

        return {
            workspaceUrl,
            apiKey: apiKey || "your-api-key",
            workflowUrl,
            baseUrl: getWorkflowEndpoint(workspaceUrl, workflowUrl),
            dataMode
        };
    }, [currentWorkspace?.url, workflow?.url, apiKey, getWorkflowEndpoint, dataMode]);

    const deployContextValue: DeployContextType = useMemo(() => {
        return {
            dataMode,
            setDataMode,
            modelStatus,
            workflowCredentials,
            hasValidWorkflowCredentials,
            apiKey,
            loading,
            setLoading,
            creatingRapidWorkflow,
            createRapidWorkflowDeploy,
            getWorkflowEndpoint
        };
    }, [
        dataMode,
        setDataMode,
        modelStatus,
        workflowCredentials,
        hasValidWorkflowCredentials,
        apiKey,
        loading,
        setLoading,
        creatingRapidWorkflow,
        createRapidWorkflowDeploy,
        getWorkflowEndpoint
    ]);

    return <DeployContext.Provider value={deployContextValue}>{children}</DeployContext.Provider>;
};
